import type { ItemFieldRow } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const validUuid = (value: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const trim = (value: string): string => value.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');
function clone<T>(value: T): T {
    if (!arrayLike(value)) return value;
    const entries = Object.entries(value);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]))) as T;
}
export interface InventoryAbilityGrantReaders {
    itemField: (row: ItemFieldRow | null, name: string) => unknown;
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
}

/** Borrow only for reading. A present null item_arr prevents the renamed
 * item_id fallback; layerless shells look up their own reference without a UUID gate. */
function borrowAbilityEntryLayerSource(entry: ItemFieldRow, readers: Pick<InventoryAbilityGrantReaders, 'resolveCarrierRef'>): ItemFieldRow {
    if (!arrayLike(entry)) throw new TypeError('Ability entry source requires a PHP array');
    const source = own(entry, 'item_arr') ?? (has(entry, 'item_arr') ? null : own(entry, 'item_id') ?? null);
    if (typeof source === 'string' && source !== '' && validUuid(source)) return readers.resolveCarrierRef(source) ?? [];
    if (arrayLike(source)) {
        if (!phpJsonTruthy(own(source, 'item_layer_types'))) {
            const id = own(own(source, 'items'), 'item_id') ?? '';
            if (typeof id === 'string' && id !== '') return readers.resolveCarrierRef(id) ?? source;
        }
        return source;
    }
    return entry;
}
/** Read-only donor for a scalar name lookup; never use this as a writable layer source. */
export function borrowAbilityEntryNameSource(entry: ItemFieldRow, readers: Pick<InventoryAbilityGrantReaders, 'resolveCarrierRef'>): ItemFieldRow {
    return borrowAbilityEntryLayerSource(entry, readers);
}
/** Public layer sources are writable, isolated copies of the selected donor. */
export function readAbilityEntryLayerSource(entry: ItemFieldRow, readers: Pick<InventoryAbilityGrantReaders, 'itemField' | 'resolveCarrierRef'>): ItemFieldRow {
    return clone(borrowAbilityEntryLayerSource(entry, readers));
}
/** Identity selection deliberately does not validate the selected string UUID.
 * The grant caller performs check_uuid after normalizing a status wrapper. */
export function readAbilityEntryId(entry: unknown, readers: Pick<InventoryAbilityGrantReaders, 'itemField' | 'resolveCarrierRef'>): string {
    if (typeof entry === 'string') return entry;
    if (!arrayLike(entry)) return '';
    for (const key of ['ability_id', 'item_arr']) {
        const id = own(entry, key);
        if (typeof id === 'string' && id !== '') return id;
    }
    const renamed = own(entry, 'item_id');
    if (!has(entry, 'item_arr') && typeof renamed === 'string' && renamed !== '') return renamed;
    const source = borrowAbilityEntryLayerSource(entry, readers);
    const id = readers.itemField(source, 'item_id') ?? readLayerListEntryUuid(source);
    return typeof id === 'string' ? id : '';
}
export function readRequiredAbilityLevel(row: ItemFieldRow | null, readers: InventoryAbilityGrantReaders): number {
    if (row !== null && !arrayLike(row)) throw new TypeError('Native level read requires a PHP array or null');
    let level = readers.layerValue(row, 'requires_level');
    if (level === null) level = readers.layerValue(row, 'requires_class_level');
    return level !== null ? phpJsonInt(level) : 1;
}
export function containsAbilityTriggerKey(row: unknown): boolean {
    if (!arrayLike(row)) return false;
    const layers = own(row, 'item_layer_types');
    return [...Object.keys(row), ...(arrayLike(layers) ? Object.keys(layers) : [])].some(key => key.includes('trigger'));
}
function mergedUniqueValues(left: ItemFieldRow, right: ItemFieldRow): unknown[] {
    const merged = new Map<string, unknown>();
    let next = 0;
    for (const row of [left, right]) for (const [key, value] of Object.entries(row)) {
        const integer = /^(?:0|-?[1-9]\d*)$/.test(key) ? BigInt(key) : null;
        const isInteger = integer !== null && integer >= -9223372036854775808n && integer <= 9223372036854775807n;
        merged.set(isInteger ? String(next++) : key, value);
    }
    const seen = new Set<string>(), out: unknown[] = [];
    for (const value of merged.values()) {
        const key = phpJsonString(value);
        if (!seen.has(key)) { seen.add(key); out.push(value); }
    }
    return out;
}
/** Native weapon grant merge: only shield_shattering_blow propagates. Existing
 * root arrays merge with PHP array_merge/string-unique behavior; canonical
 * ability layers are not the append destination of this particular helper. */
export function mergeWeaponInventoryAttackLayers(ability: ItemFieldRow, inventory: ItemFieldRow, readers: InventoryAbilityGrantReaders): ItemFieldRow {
    if (!arrayLike(ability) || !arrayLike(inventory)) throw new TypeError('Weapon grant merge requires PHP arrays');
    if ((readers.itemField(inventory, 'item_type') ?? '') !== 'weapon') return clone(ability);
    const out = Object.fromEntries(Object.entries(ability));
    const key = 'shield_shattering_blow', values = readers.layerEntries(inventory, key);
    if (!Object.values(values).some(value => value !== null && trim(phpJsonString(value)) !== '')) return clone(out);
    const existing = own(out, key);
    out[key] = !phpJsonTruthy(existing) || !arrayLike(existing) ? clone(values) : mergedUniqueValues(existing, values);
    return clone(out);
}
function borrowArrayShape(value: ItemFieldRow): ItemFieldRow {
    return Array.isArray(value) || Object.keys(value).some((key, index) => key !== String(index)) ? value : Object.values(value);
}
/** Internal read-only merge. Untouched definition branches stay borrowed; only
 * the propagated root layer is owned. Consumers must copy any other path they write.
 * The public writable merge above deliberately retains its isolation contract. */
export function borrowWeaponInventoryAttackLayers(ability: ItemFieldRow, inventory: ItemFieldRow, readers: InventoryAbilityGrantReaders): ItemFieldRow {
    if (!arrayLike(ability) || !arrayLike(inventory)) throw new TypeError('Weapon grant merge requires PHP arrays');
    if ((readers.itemField(inventory, 'item_type') ?? '') !== 'weapon') return borrowArrayShape(ability);
    const key = 'shield_shattering_blow', values = readers.layerEntries(inventory, key);
    if (!Object.values(values).some(value => value !== null && trim(phpJsonString(value)) !== '')) return borrowArrayShape(ability);
    const out = Object.fromEntries(Object.entries(ability)), existing = own(ability, key);
    out[key] = clone(!phpJsonTruthy(existing) || !arrayLike(existing) ? values : mergedUniqueValues(existing, values));
    return out;
}
export function createInventoryAbilityGrantHelpers(deps: FormulaReadDependencies) {
    const context = createFormulaReadContext(deps);
    const readers: InventoryAbilityGrantReaders = {...context, resolveCarrierRef: id => {
        deps.trace?.(['reference', id]); return deps.resolveCarrierRef(id);
    }};
    return {
        abilityEntryId: (entry: unknown) => readAbilityEntryId(entry, readers),
        abilityEntryLayerSource: (entry: ItemFieldRow) => readAbilityEntryLayerSource(entry, readers),
        requiresLevel: (row: ItemFieldRow | null) => readRequiredAbilityLevel(row, readers),
        containsTrigger: containsAbilityTriggerKey,
        mergeWeapon: (ability: ItemFieldRow, inventory: ItemFieldRow) => mergeWeaponInventoryAttackLayers(ability, inventory, readers),
        borrowWeapon: (ability: ItemFieldRow, inventory: ItemFieldRow) => borrowWeaponInventoryAttackLayers(ability, inventory, readers),
    };
}
